/**
 * JSON control messages — docs/PROTOCOL.md §4.
 *
 * Validators are hand-written rather than schema-library driven so the web bundle
 * carries no extra dependency and so each rejection can name the exact field. They
 * are intentionally *narrowing* — an unknown `t` is not an error (§4), it is
 * ignored, which is the forward-compatibility hatch.
 */
import { LIMITS, PROTOCOL_VERSION } from './limits.js';
// ── errors ───────────────────────────────────────────────────────────────────
export const ErrorCode = {
    BAD_HELLO: 'bad-hello',
    UNSUPPORTED_VERSION: 'unsupported-version',
    UNAUTHORIZED: 'unauthorized',
    PASSCODE_REQUIRED: 'passcode-required',
    PASSCODE_INVALID: 'passcode-invalid',
    SESSION_NOT_FOUND: 'session-not-found',
    SESSION_OCCUPIED: 'session-occupied',
    SESSION_FULL: 'session-full',
    UNSUPPORTED_FORMAT: 'unsupported-format',
    FRAME_TOO_LARGE: 'frame-too-large',
    RATE_LIMITED: 'rate-limited',
    INTERNAL: 'internal',
};
/** WebSocket close codes — docs/PROTOCOL.md §5. */
export const CloseCode = {
    NORMAL: 1000,
    GOING_AWAY: 1001,
    /** Listener could not keep up and was dropped — §6. Retryable, but back off first. */
    POLICY_VIOLATION: 1008,
    /** Session is at capacity. Retryable: a listener slot may free up later. */
    TRY_AGAIN_LATER: 1013,
    PROTOCOL_ERROR: 4400,
    UNAUTHORIZED: 4401,
    NOT_FOUND: 4404,
    OCCUPIED: 4409,
    RATE_LIMITED: 4429,
    INTERNAL: 4500,
};
const CLOSE_FOR_ERROR = {
    [ErrorCode.BAD_HELLO]: CloseCode.PROTOCOL_ERROR,
    [ErrorCode.UNSUPPORTED_VERSION]: CloseCode.PROTOCOL_ERROR,
    [ErrorCode.UNAUTHORIZED]: CloseCode.UNAUTHORIZED,
    [ErrorCode.PASSCODE_REQUIRED]: CloseCode.UNAUTHORIZED,
    [ErrorCode.PASSCODE_INVALID]: CloseCode.UNAUTHORIZED,
    [ErrorCode.SESSION_NOT_FOUND]: CloseCode.NOT_FOUND,
    [ErrorCode.SESSION_OCCUPIED]: CloseCode.OCCUPIED,
    [ErrorCode.SESSION_FULL]: CloseCode.TRY_AGAIN_LATER,
    [ErrorCode.UNSUPPORTED_FORMAT]: CloseCode.PROTOCOL_ERROR,
    [ErrorCode.FRAME_TOO_LARGE]: CloseCode.PROTOCOL_ERROR,
    [ErrorCode.RATE_LIMITED]: CloseCode.RATE_LIMITED,
    [ErrorCode.INTERNAL]: CloseCode.INTERNAL,
};
export function closeCodeFor(code) {
    return CLOSE_FOR_ERROR[code] ?? CloseCode.INTERNAL;
}
/**
 * Whether a client should reconnect on its own after this close code.
 * The 4400..4429 band all need a human to change something first — a bad key, a
 * wrong passcode, a session someone else owns — so retrying only hides the problem.
 *
 * 1008 (dropped for falling behind) and 1013 (session full) are retryable, but only
 * behind a backoff: an immediate reconnect on either will almost certainly hit the
 * same wall. Use {@link reconnectFloorMs} for the minimum first delay.
 */
export function isRetryableClose(code) {
    return !(code >= 4400 && code <= 4429);
}
/**
 * Minimum delay before the first reconnect attempt after this close code.
 *
 * A link that could not sustain the stream, or a session that was at capacity, needs
 * time for the underlying condition to change. Everything else can retry promptly and
 * let normal exponential backoff take over.
 */
export function reconnectFloorMs(code) {
    switch (code) {
        case CloseCode.POLICY_VIOLATION:
            return 5_000;
        case CloseCode.TRY_AGAIN_LATER:
            return 10_000;
        default:
            return 0;
    }
}
function fail(error) {
    return { ok: false, error };
}
function isRecord(v) {
    return typeof v === 'object' && v !== null && !Array.isArray(v);
}
function isNonEmptyString(v) {
    return typeof v === 'string' && v.length > 0;
}
/**
 * Parse a control frame. Returns the discriminated union on success.
 *
 * `hello` is validated strictly because it decides auth and codec setup. Everything
 * else is validated loosely — a malformed `ping` should not kill a live stream.
 */
export function parseClientMessage(raw) {
    if (raw.length > LIMITS.MAX_CONTROL_BYTES) {
        return fail(`control message of ${raw.length} B exceeds ${LIMITS.MAX_CONTROL_BYTES} B`);
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return fail('control message is not valid JSON');
    }
    if (!isRecord(parsed))
        return fail('control message must be a JSON object');
    const t = parsed['t'];
    if (typeof t !== 'string')
        return fail('control message is missing a string "t"');
    switch (t) {
        case 'hello':
            return parseHello(parsed);
        case 'meta':
            return isNonEmptyString(parsed['name'])
                ? { ok: true, value: { t: 'meta', name: parsed['name'].slice(0, 200) } }
                : fail('"meta" needs a non-empty "name"');
        case 'ping':
            return {
                ok: true,
                value: { t: 'ping', ts: typeof parsed['ts'] === 'number' ? parsed['ts'] : 0 },
            };
        case 'bye':
            return { ok: true, value: { t: 'bye' } };
        default:
            return fail(`unknown message type "${t}"`);
    }
}
function parseHello(o) {
    const v = o['v'];
    if (typeof v !== 'number')
        return fail('"hello" is missing a numeric "v"');
    if (v !== PROTOCOL_VERSION) {
        return fail(`unsupported protocol version ${v}, this relay speaks ${PROTOCOL_VERSION}`);
    }
    const role = o['role'];
    if (role !== 'sender' && role !== 'listener') {
        return fail('"hello" needs role "sender" or "listener"');
    }
    const passcodeOf = (value) => {
        if (value === undefined || value === null || value === '')
            return null;
        return typeof value === 'string' ? value : null;
    };
    if (role === 'listener') {
        return {
            ok: true,
            value: { t: 'hello', v, role: 'listener', passcode: passcodeOf(o['passcode']) },
        };
    }
    const formatResult = parseFormat(o['format']);
    if (!formatResult.ok)
        return fail(formatResult.error);
    const sessionRaw = isRecord(o['session']) ? o['session'] : {};
    const nameRaw = sessionRaw['name'];
    const resumeRaw = sessionRaw['resume'];
    const hello = {
        t: 'hello',
        v,
        role: 'sender',
        format: formatResult.value,
        session: {
            name: isNonEmptyString(nameRaw) ? nameRaw.slice(0, 200) : 'Untitled session',
            passcode: passcodeOf(sessionRaw['passcode']),
            resume: isNonEmptyString(resumeRaw) ? resumeRaw : null,
        },
    };
    if (isRecord(o['client'])) {
        const c = o['client'];
        hello.client = {
            name: isNonEmptyString(c['name']) ? c['name'].slice(0, 80) : 'unknown',
            version: isNonEmptyString(c['version']) ? c['version'].slice(0, 40) : '0',
            ...(isNonEmptyString(c['daw']) ? { daw: c['daw'].slice(0, 80) } : {}),
        };
    }
    return { ok: true, value: hello };
}
export function parseFormat(value) {
    if (!isRecord(value))
        return fail('"format" must be an object');
    const codec = value['codec'];
    if (codec !== 'opus' && codec !== 'pcm')
        return fail(`unsupported codec "${String(codec)}"`);
    const sampleRate = value['sampleRate'];
    if (typeof sampleRate !== 'number' || !LIMITS.SUPPORTED_SAMPLE_RATES.includes(sampleRate)) {
        return fail(`sampleRate ${String(sampleRate)} is not supported ` +
            `(want one of ${LIMITS.SUPPORTED_SAMPLE_RATES.join(', ')})`);
    }
    const channels = value['channels'];
    if (typeof channels !== 'number' || !LIMITS.SUPPORTED_CHANNELS.includes(channels)) {
        return fail(`channels ${String(channels)} is not supported (want 1 or 2)`);
    }
    const frameMs = value['frameMs'];
    if (typeof frameMs !== 'number' || !LIMITS.SUPPORTED_FRAME_MS.includes(frameMs)) {
        return fail(`frameMs ${String(frameMs)} is not supported ` +
            `(want one of ${LIMITS.SUPPORTED_FRAME_MS.join(', ')})`);
    }
    const [minBitrate, maxBitrate] = LIMITS.BITRATE_RANGE;
    const bitrate = value['bitrate'];
    if (typeof bitrate !== 'number' || bitrate < minBitrate || bitrate > maxBitrate) {
        return fail(`bitrate ${String(bitrate)} is outside ${minBitrate}..${maxBitrate}`);
    }
    return { ok: true, value: { codec, sampleRate, channels, frameMs, bitrate } };
}
/** Parse a relay→client message. Unknown types resolve to `null`, per §4. */
export function parseServerMessage(raw) {
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    if (!isRecord(parsed) || typeof parsed['t'] !== 'string')
        return null;
    const known = new Set([
        'ready',
        'stream-start',
        'stream-end',
        'listeners',
        'stats',
        'meta',
        'pong',
        'error',
    ]);
    return known.has(parsed['t']) ? parsed : null;
}
export function serialize(msg) {
    return JSON.stringify(msg);
}
//# sourceMappingURL=messages.js.map